import {
  AppsListDetailRegular,
  ArrowSyncRegular,
  DataAreaRegular,
  DatabaseSearchRegular,
  GridRegular,
  HomeRegular,
  PersonCircleRegular,
  SearchRegular,
  SettingsRegular,
  WeatherMoonRegular,
  WeatherSunnyRegular,
} from '@fluentui/react-icons';
import { Button, FluentProvider, webDarkTheme, webLightTheme } from '@fluentui/react-components';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { queries } from './api.js';
import { NavLink, useLocation, useNavigate } from './router.js';
import {
  CatalogPage,
  ConnectionsPage,
  AgentPage,
  GovernancePage,
  GraphPage,
  KpiRecordPage,
  MeasuresPage,
  OverviewPage,
  ReviewPage,
  SandboxPage,
  SearchPage,
  SettingsPage,
} from './Pages.js';

const nav = [
  {
    to: '/overview',
    label: 'Overview',
    icon: HomeRegular,
    activeFor: ['/overview', '/governance'],
  },
  {
    to: '/reviews',
    label: 'KPI Management',
    icon: AppsListDetailRegular,
    activeFor: ['/reviews', '/catalog', '/agent', '/sandbox'],
  },
  {
    to: '/graph',
    label: 'Graph Explorer',
    icon: GridRegular,
    activeFor: ['/graph', '/measures'],
  },
];

const adminNav = [
  {
    to: '/connections',
    label: 'Connections',
    icon: DatabaseSearchRegular,
    activeFor: ['/connections'],
  },
  { to: '/settings', label: 'Settings', icon: SettingsRegular, activeFor: ['/settings'] },
];

function Sidebar() {
  return (
    <aside className="sidebar">
      <div className="brand">
        <div className="brand-mark">
          <DataAreaRegular />
        </div>
        <div>
          <strong>MetricGraph</strong>
          <span>Trusted KPI intelligence</span>
        </div>
      </div>
      <nav aria-label="Workspace">
        <span className="nav-heading">Workspace</span>
        {nav.map(({ to, label, icon: Icon, activeFor }) => (
          <NavLink
            key={to}
            to={to}
            activeFor={activeFor}
            aria-label={label}
            className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
          >
            <Icon aria-hidden />
            <span>{label}</span>
          </NavLink>
        ))}
        <span className="nav-heading admin">Administration</span>
        {adminNav.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            aria-label={label}
            className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
          >
            <Icon aria-hidden />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="connector-status">
        <span className="pulse-dot" />
        <div>
          <strong>Fabric connector</strong>
          <span>Metadata provider · see Connections</span>
        </div>
        <ArrowSyncRegular aria-hidden />
      </div>
    </aside>
  );
}

function AppHeader({
  theme,
  setTheme,
}: {
  theme: 'light' | 'dark';
  setTheme: (value: 'light' | 'dark') => void;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const scopes = useQuery({ queryKey: ['scopes'], queryFn: queries.scopes });
  const session = useQuery({ queryKey: ['session'], queryFn: queries.session });
  const activeScope = scopes.data?.items.find(({ status }) => status === 'ACTIVE');
  const actor = session.data?.actor;
  const title =
    [...nav, ...adminNav].find(({ to, activeFor }) =>
      (activeFor ?? [to]).some(
        (path) => location.pathname === path || location.pathname.startsWith(`${path}/`),
      ),
    )?.label ?? 'KPI Record';
  return (
    <header className="app-header">
      <div className="breadcrumb">
        <span>{activeScope?.name ?? 'KPI scope'}</span>
        <span>/</span>
        <strong>{title}</strong>
      </div>
      <form
        className="global-search"
        role="search"
        onSubmit={(event) => {
          event.preventDefault();
          if (query.trim()) void navigate(`/search?q=${encodeURIComponent(query.trim())}`);
        }}
      >
        <SearchRegular aria-hidden />
        <input
          aria-label="Search MetricGraph"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search KPIs, measures, reports…"
        />
        <kbd>⌘ K</kbd>
      </form>
      <div className="header-control" aria-label="Current scope">
        <span>
          <small>Scope</small>
          {activeScope?.name ?? (scopes.isLoading ? 'Loading…' : 'No active scope')}
        </span>
      </div>
      <label className="date-control">
        <small>Effective date</small>
        <input type="date" defaultValue={new Date().toISOString().slice(0, 10)} />
      </label>
      <Button
        appearance="subtle"
        icon={theme === 'dark' ? <WeatherSunnyRegular /> : <WeatherMoonRegular />}
        aria-label={`Use ${theme === 'dark' ? 'light' : 'dark'} theme`}
        onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
      />
      <div className="profile" aria-label="Current user">
        <PersonCircleRegular />
        <span>
          <strong>{actor?.id ?? 'Unauthenticated'}</strong>
          <small>{actor?.roles.join(' · ') ?? 'No active roles'}</small>
        </span>
      </div>
    </header>
  );
}

export default function App() {
  const staticDemo = import.meta.env.VITE_METRICGRAPH_STATIC_DEMO === 'true';
  const [theme, setTheme] = useState<'light' | 'dark'>(() =>
    localStorage.getItem('metricgraph-theme') === 'light' ? 'light' : 'dark',
  );
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem('metricgraph-theme', theme);
  }, [theme]);
  return (
    <FluentProvider
      theme={theme === 'dark' ? webDarkTheme : webLightTheme}
      className={`app-provider${staticDemo ? ' static-demo' : ''}`}
    >
      <a className="skip-link" href="#main-content">
        Skip to main content
      </a>
      {staticDemo ? (
        <div className="static-demo-banner" role="status">
          <strong>GitHub Pages demo</strong>
          <span>
            Representative synthetic metadata · session-local interactions · no tenant access or
            production writes
          </span>
          <a href="https://github.com/cz1993/MetricGraph">View source</a>
        </div>
      ) : null}
      <div className="app-shell">
        <Sidebar />
        <div className="workspace">
          <AppHeader theme={theme} setTheme={setTheme} />
          <main id="main-content">
            <RouteContent />
          </main>
        </div>
      </div>
    </FluentProvider>
  );
}

function RouteContent() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (
      pathname === '/' ||
      ![
        '/overview',
        '/catalog',
        '/measures',
        '/reviews',
        '/search',
        '/graph',
        '/sandbox',
        '/agent',
        '/governance',
        '/connections',
        '/settings',
      ].some((route) => pathname === route || pathname.startsWith(`${route}/`))
    ) {
      navigate('/overview', { replace: true });
    }
  }, [navigate, pathname]);
  if (pathname === '/catalog') return <CatalogPage />;
  if (pathname.startsWith('/catalog/')) return <KpiRecordPage />;
  if (pathname === '/measures') return <MeasuresPage />;
  if (pathname === '/reviews') return <ReviewPage />;
  if (pathname === '/search') return <SearchPage />;
  if (pathname === '/graph') return <GraphPage />;
  if (pathname === '/sandbox') return <SandboxPage />;
  if (pathname === '/agent') return <AgentPage />;
  if (pathname === '/governance') return <GovernancePage />;
  if (pathname === '/connections') return <ConnectionsPage />;
  if (pathname === '/settings') return <SettingsPage />;
  return <OverviewPage />;
}
